import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, FileText, CreditCard, Wallet, Trophy, Users, HelpingHand, Receipt, Gift, CheckCircle2 } from "lucide-react";
import api, { fmtINR, fmtDate } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import LangToggle from "@/components/LangToggle";

export default function CustomerDashboard() {
  const nav = useNavigate();
  const { user, tenant } = useAuth();
  const { t } = useI18n();
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/customer/dashboard").then((r) => setD(r.data)).catch(() => {});
  }, []);

  if (!d) {
    return <div className="p-6 text-center text-slate-500" data-testid="dashboard-loading">Loading...</div>;
  }

  const progress = d.policy?.premium_amount
    ? Math.min(100, Math.round(((d.policy.collected_amount || 0) / d.policy.premium_amount) * 100))
    : 0;

  return (
    <div data-testid="customer-dashboard">
      {/* Header */}
      <div className="bg-tenant-gradient text-white px-5 pt-6 pb-20 rounded-b-[2rem] relative overflow-hidden">
        <div className="absolute inset-0 opacity-25 pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 100% 0%, rgba(212,175,55,0.6), transparent 50%)" }} />
        <div className="relative z-10 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-white/15 backdrop-blur grid place-items-center text-tenant-accent font-extrabold text-lg">
              {(d.customer?.full_name || "U").charAt(0)}
            </div>
            <div>
              <div className="text-[11px] opacity-80">{t("hello")},</div>
              <div className="font-extrabold tracking-tight">{d.customer?.full_name || user?.full_name} 👋</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <LangToggle />
            <button data-testid="goto-notifications-btn" onClick={() => nav("/customer/notifications")} className="relative w-9 h-9 rounded-full bg-white/15 grid place-items-center">
              <Bell className="w-4 h-4" />
              {d.unread_notifications > 0 && (
                <span data-testid="notif-badge" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-tenant-accent text-tenant-primary rounded-full text-[10px] font-bold grid place-items-center">
                  {d.unread_notifications}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Policy summary */}
        {d.policy && (
          <div className="relative z-10 mt-5 bg-white/10 backdrop-blur-md rounded-2xl p-4 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] opacity-80">Policy No</div>
              <div className="font-bold tracking-tight">{d.policy.policy_number}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.2em] opacity-80">Next Due</div>
              <div className="font-bold tracking-tight text-tenant-accent">{fmtDate(d.policy.due_date)}</div>
            </div>
          </div>
        )}
      </div>

      {/* Wallet cards */}
      <div className="px-5 -mt-14 relative z-10 grid grid-cols-2 gap-3">
        <button data-testid="saving-wallet-card" onClick={() => nav("/customer/wallet")} className="saving-wallet-grad text-white rounded-3xl p-4 text-left card-shadow-lg tap-scale">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] opacity-90">
            <Wallet className="w-3 h-3" /> Saving Wallet
          </div>
          <div className="mt-2 text-[10px] opacity-80">{t("available_balance")}</div>
          <div data-testid="saving-balance" className="text-2xl font-extrabold tracking-tight mt-0.5">{fmtINR(d.wallet.saving_balance)}</div>
        </button>
        <button data-testid="bonus-wallet-card" onClick={() => nav("/customer/bonus")} className="bonus-wallet-grad text-white rounded-3xl p-4 text-left card-shadow-lg tap-scale">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] opacity-90">
            <Gift className="w-3 h-3" /> Bonus Wallet
          </div>
          <div className="mt-2 text-[10px] opacity-80">{t("available_balance")}</div>
          <div data-testid="bonus-balance" className="text-2xl font-extrabold tracking-tight mt-0.5">{fmtINR(d.wallet.bonus_balance)}</div>
        </button>
      </div>

      <div className="px-5 mt-3">
        <div className="gold-grad rounded-2xl p-4 flex items-center justify-between text-tenant-primary">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] opacity-80 font-bold">{t("total_savings")}</div>
            <div data-testid="total-savings" className="text-2xl font-extrabold tracking-tight">{fmtINR(d.wallet.total_savings)}</div>
          </div>
          <div className="text-[10px] font-bold opacity-80 text-right">
            Saving + Bonus<br/>combined
          </div>
        </div>
      </div>

      {/* Today's collection */}
      <div className="px-5 mt-4">
        <div className="bg-white border border-slate-100 rounded-2xl p-4 card-shadow flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-400">{t("daily_collection")} Today</div>
            <div className="mt-1 font-extrabold text-lg">
              <span data-testid="today-collected">{fmtINR(d.today_collection)}</span>
              <span className="text-slate-400 text-sm font-semibold"> / {fmtINR(d.daily_target)}</span>
            </div>
            <div className="mt-2 h-2 w-44 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full emerald-grad" style={{ width: `${Math.min(100, (d.today_collection / Math.max(1, d.daily_target)) * 100)}%` }} />
            </div>
          </div>
          {d.today_collection >= d.daily_target && d.daily_target > 0 ? (
            <span data-testid="collection-status-completed" className="inline-flex items-center gap-1 text-emerald-600 text-xs font-bold bg-emerald-50 rounded-full px-3 py-1.5">
              <CheckCircle2 className="w-3.5 h-3.5" /> {t("completed")}
            </span>
          ) : (
            <span className="text-amber-600 text-xs font-bold bg-amber-50 rounded-full px-3 py-1.5">Due</span>
          )}
        </div>
      </div>

      {/* Quick actions */}
      <div className="px-5 mt-5">
        <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-400 mb-3">{t("quick_actions")}</div>
        <div className="grid grid-cols-4 gap-3">
          <QuickAction icon={Receipt} label="Pay Premium" tone="amber" onClick={() => nav("/customer/policy")} testId="qa-pay-premium" />
          <QuickAction icon={CreditCard} label="Daily Collection" tone="emerald" onClick={() => nav("/customer/daily-collection")} testId="qa-collection" />
          <QuickAction icon={FileText} label="Policy" tone="blue" onClick={() => nav("/customer/policy")} testId="qa-policy" />
          <QuickAction icon={Wallet} label="Wallets" tone="violet" onClick={() => nav("/customer/wallet")} testId="qa-wallets" />
          <QuickAction icon={Gift} label="Benefits" tone="rose" onClick={() => nav("/customer/benefits")} testId="qa-benefits" />
          <QuickAction icon={Users} label="Referral" tone="cyan" onClick={() => nav("/customer/referral")} testId="qa-referral" />
          <QuickAction icon={Trophy} label="Lucky Draw" tone="amber" onClick={() => nav("/customer/lucky-draw")} testId="qa-lucky-draw" />
          <QuickAction icon={HelpingHand} label="Finance" tone="indigo" onClick={() => nav("/customer/finance-support")} testId="qa-finance" />
        </div>
      </div>

      <div className="h-10" />
    </div>
  );
}

const tones = {
  amber: "bg-amber-50 text-amber-600",
  emerald: "bg-emerald-50 text-emerald-600",
  blue: "bg-blue-50 text-blue-600",
  violet: "bg-violet-50 text-violet-600",
  rose: "bg-rose-50 text-rose-600",
  cyan: "bg-cyan-50 text-cyan-600",
  indigo: "bg-indigo-50 text-indigo-600",
};

const QuickAction = ({ icon: Icon, label, tone = "blue", onClick, testId }) => (
  <button data-testid={testId} onClick={onClick} className="flex flex-col items-center gap-1.5 tap-scale">
    <div className={`w-14 h-14 rounded-2xl ${tones[tone]} grid place-items-center`}>
      <Icon className="w-6 h-6" strokeWidth={2} />
    </div>
    <span className="text-[10px] text-slate-700 font-semibold text-center leading-tight">{label}</span>
  </button>
);
